import { getToken } from '../../services/tokenStore';
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from '../../config/axios';
import ScrollToTop from '../common/ScrollToTop';
import { colors } from '../../theme/tokens';
import {
  SPONSOR_ADDABLE_TYPE_LIST,
  createEmptySponsorEntry,
  ensureRequiredSponsorEntry,
  countEntriesByType,
  getSponsorFieldDef,
  groupSponsorEntriesByType,
  insertSponsorEntry,
  sortSponsorEntriesByType,
  sponsorEntriesFromInitialData,
  serializeSponsorEntries,
} from '../../utils/administrativeSponsorFields';
import {
  availableRegulatoryTypesForDropdown,
  createEmptyRegulatoryEntry,
  getRegulatoryIdentifierDef,
  regulatoryEntriesFromInitialData,
  serializeRegulatoryEntries,
} from '../../utils/administrativeRegulatoryIdentifiers';
import { useProject } from '../../context/ProjectContext';
import {
  getProjectIdentityFields,
  PROJECT_IDENTITY_SOURCE_HINT,
  resolveProjectSponsorName,
  withProjectIdentityFields,
} from '../../utils/projectIdentityFields';
import {
  ADMIN_FIELD_HOVER_INFO,
  REGULATORY_IDENTIFIER_HOVER_BY_TYPE,
  SPONSOR_NAME_HOVER_BY_TYPE,
  SPONSOR_ADD_HOVER_BY_TYPE,
} from '../../data/administrativeFieldHoverInfo';

const API_URL = `${(
  process.env.REACT_APP_API_URL ||
  'https://backend-clinical-intake.dizzaroo.com'
).replace(/\/$/, '')}/api`;

const compactEntryRowSx = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1,
  width: '100%',
};

const compactEntryActionsSx = {
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  pt: 0.5,
};

const SPONSOR_APPROVAL_TYPE_OPTIONS = [
  { value: 'C132352', label: 'Sponsor Approval Date (C132352)' },
  { value: 'CNEW', label: 'Location of Sponsor Approval Date (CNEW)' },
];

const ADMINISTRATIVE_DATE_FIELDS = {
  sponsor_approval_date: 'Sponsor Approval Date',
};

const HoverField = ({ title, children, sx }) => {
  if (!title) return children;
  return (
    <Tooltip
      title={title}
      arrow
      placement="top-start"
      enterDelay={450}
      enterNextDelay={450}
      componentsProps={{ tooltip: { sx: { maxWidth: 420 } } }}
    >
      <Box component="span" sx={{ display: 'block', width: '100%', ...sx }}>
        {children}
      </Box>
    </Tooltip>
  );
};

/** YYYY-MM-DD in local timezone — minimum selectable date (today). */
const getTodayDateInputMin = () => {
  const today = new Date();
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, '0');
  const d = String(today.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const normalizeDateInputValue = (value) => {
  if (!value) return '';
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  try {
    const parsed = new Date(s);
    if (Number.isNaN(parsed.getTime())) return s.slice(0, 10);
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, '0');
    const d = String(parsed.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  } catch {
    return s.slice(0, 10);
  }
};

const isDateBeforeToday = (value) => {
  const normalized = normalizeDateInputValue(value);
  if (!normalized || !/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return false;
  return normalized < getTodayDateInputMin();
};

/** Decimals allowed: greater than 0 and at most 1 (e.g. 0.11, 0.5, 1). No negatives or values above 1. */
const isValidVersionNumber = (value) => {
  const s = String(value ?? '').trim();
  if (!s) return true;
  if (!/^\d+(\.\d+)?$/.test(s)) return false;
  const n = Number(s);
  return !Number.isNaN(n) && n > 0 && n <= 1;
};

const isVersionNumberInputAllowed = (raw) => {
  const s = String(raw);
  if (s === '') return true;
  if (s.startsWith('-') || !/^\d*\.?\d*$/.test(s)) return false;
  const n = Number(s);
  if (!Number.isNaN(n) && n > 1) return false;
  return true;
};

const formatVersionNumberFromApi = (value) => {
  if (value === undefined || value === null || value === '') return '';
  const s = String(value).trim();
  if (!isValidVersionNumber(s)) return s;
  const n = Number(s);
  if (n === 1) return '1';
  return String(parseFloat(n.toFixed(4)));
};

const validateAdministrativeDates = (data) => {
  const invalid = [];
  Object.entries(ADMINISTRATIVE_DATE_FIELDS).forEach(([field, label]) => {
    const raw = data[field];
    if (!raw || !String(raw).trim()) return;
    if (isDateBeforeToday(raw)) invalid.push(label);
  });
  if (invalid.length === 0) return null;
  return `${invalid.join(' and ')} must be today or a future date (past dates are not allowed).`;
};

const AdministrativeInfoForm = ({ onSubmit, initialData, projectId }) => {
  const { currentProject } = useProject();
  const [formData, setFormData] = useState({
    sponsor_protocol_identifier: initialData?.sponsor_protocol_identifier || '',
    original_protocol_indicator: initialData?.original_protocol_indicator || '',
    amendment_identifier: initialData?.amendment_identifier || '',
    amendment_scope: initialData?.amendment_scope || '',
    document_header: initialData?.document_header || '',
    sponsor_approval_date: normalizeDateInputValue(initialData?.sponsor_approval_date) || '',
    sponsor_approval_location: initialData?.sponsor_approval_location || '',
    sponsor_approval_location_text: initialData?.sponsor_approval_location_text || '',
    medical_expert_contact: initialData?.medical_expert_contact || '',
    sponsor_investigational_product_codes: initialData?.sponsor_investigational_product_codes || '',
    investigational_product_names: initialData?.investigational_product_names || ''
  });

  const [sponsorInfoEntries, setSponsorInfoEntries] = useState(() =>
    ensureRequiredSponsorEntry(
      sortSponsorEntriesByType(sponsorEntriesFromInitialData(initialData))
    )
  );
  const [selectedSponsorFieldType, setSelectedSponsorFieldType] = useState('sponsor');

  const [regulatoryIdentifierEntries, setRegulatoryIdentifierEntries] = useState(() =>
    regulatoryEntriesFromInitialData(initialData)
  );
  const [selectedRegulatoryType, setSelectedRegulatoryType] = useState('eu_ct');

  const regulatoryDropdownOptions = useMemo(
    () => availableRegulatoryTypesForDropdown(regulatoryIdentifierEntries),
    [regulatoryIdentifierEntries]
  );

  // Sync form state when initialData loads (e.g. after reload / getLatest)
  useEffect(() => {
    if (!initialData) return;
    let sponsorApprovalLocation = initialData.sponsor_approval_location ?? '';
    if (!sponsorApprovalLocation && initialData.sponsor_approval_date) {
      sponsorApprovalLocation = 'C132352';
    }
    if (!sponsorApprovalLocation && initialData.sponsor_approval_location_text) {
      sponsorApprovalLocation = 'CNEW';
    }

    setFormData({
      sponsor_protocol_identifier: initialData.sponsor_protocol_identifier ?? '',
      original_protocol_indicator: initialData.original_protocol_indicator ?? '',
      amendment_identifier: initialData.amendment_identifier ?? '',
      amendment_scope: initialData.amendment_scope ?? '',
      document_header: initialData.document_header ?? '',
      sponsor_approval_date: normalizeDateInputValue(initialData.sponsor_approval_date) ?? '',
      sponsor_approval_location: sponsorApprovalLocation,
      sponsor_approval_location_text: initialData.sponsor_approval_location_text ?? '',
      medical_expert_contact: initialData.medical_expert_contact ?? '',
      sponsor_investigational_product_codes: initialData.sponsor_investigational_product_codes ?? '',
      investigational_product_names: initialData.investigational_product_names ?? '',
    });
    setSponsorInfoEntries(
      ensureRequiredSponsorEntry(
        sortSponsorEntriesByType(sponsorEntriesFromInitialData(initialData))
      )
    );
    const regEntries = regulatoryEntriesFromInitialData(initialData);
    setRegulatoryIdentifierEntries(regEntries);
    const available = availableRegulatoryTypesForDropdown(regEntries);
    if (available.length > 0) {
      setSelectedRegulatoryType(available[0].key);
    }
  }, [
    initialData?.sponsor_name,
    initialData?.sponsor_entries,
    initialData?.co_sponsor_entries,
    initialData?.local_sponsor_entries,
    initialData?.device_manufacturer_entries,
    initialData?.collaborator_entries,
    initialData?.collaborators,
    initialData?.document_header,
    initialData?.sponsor_protocol_identifier,
    initialData?.original_protocol_indicator,
    initialData?.amendment_identifier,
    initialData?.amendment_scope,
    initialData?.eu_ct_number,
    initialData?.fda_ind_number,
    initialData?.nct_number,
    initialData?.regulatory_identifier_entries,
  ]);

  // Canonical trial identity comes from the project (trial create/edit dialog).
  useEffect(() => {
    if (!currentProject) return;
    const identity = getProjectIdentityFields(currentProject);
    setFormData((prev) => ({
      ...prev,
      sponsor_protocol_identifier:
        identity.sponsorProtocolIdentifier || prev.sponsor_protocol_identifier,
    }));
    if (identity.sponsorName) {
      setSponsorInfoEntries((prev) => {
        const next = prev.map((entry) => ({ ...entry }));
        const sponsorIndex = next.findIndex((entry) => entry.type === 'sponsor');
        if (sponsorIndex >= 0) {
          next[sponsorIndex] = { ...next[sponsorIndex], name: identity.sponsorName };
        }
        return next;
      });
    }
  }, [
    currentProject?._id,
    currentProject?.name,
    currentProject?.sponsorName,
    currentProject?.description,
    currentProject?.studyId,
  ]);

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [hasSaved, setHasSaved] = useState(false);

  const minSelectableDate = useMemo(() => getTodayDateInputMin(), []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (ADMINISTRATIVE_DATE_FIELDS[name]) {
      if (value && isDateBeforeToday(value)) {
        setError(
          `${ADMINISTRATIVE_DATE_FIELDS[name]} cannot be earlier than today. Please select today or a future date.`
        );
        return;
      }
      setError(null);
      setHasSaved(false);
      setFormData((prev) => ({
        ...prev,
        [name]: normalizeDateInputValue(value),
      }));
      return;
    }
    setHasSaved(false);
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSponsorApprovalTypeChange = (e) => {
    const mode = e.target.value;
    setHasSaved(false);
    setFormData((prev) => ({
      ...prev,
      sponsor_approval_location: mode,
      ...(mode === 'C132352'
        ? { sponsor_approval_location_text: '' }
        : { sponsor_approval_date: '' }),
    }));
  };

  const sponsorApprovalMode = formData.sponsor_approval_location;
  const showSponsorApprovalDate = sponsorApprovalMode === 'C132352';
  const showSponsorApprovalLocation = sponsorApprovalMode === 'CNEW';

  const handleAddSponsorInfoEntry = (typeOverride) => {
    const type = typeOverride || selectedSponsorFieldType;
    if (!type || !SPONSOR_ADDABLE_TYPE_LIST.some((d) => d.key === type)) return;
    setHasSaved(false);
    setSponsorInfoEntries((prev) =>
      insertSponsorEntry(prev, createEmptySponsorEntry(type))
    );
  };

  const handleSponsorEntryChange = (entryId, field, value) => {
    setHasSaved(false);
    setSponsorInfoEntries((prev) =>
      prev.map((entry) =>
        entry.id === entryId ? { ...entry, [field]: value } : entry
      )
    );
  };

  const handleRemoveSponsorInfoEntry = (entryId) => {
    const target = sponsorInfoEntries.find((e) => e.id === entryId);
    if (target?.type === 'sponsor' && countEntriesByType(sponsorInfoEntries, 'sponsor') <= 1) {
      return;
    }
    setHasSaved(false);
    setSponsorInfoEntries((prev) =>
      sortSponsorEntriesByType(prev.filter((entry) => entry.id !== entryId))
    );
  };

  const sponsorGroups = useMemo(
    () => groupSponsorEntriesByType(sponsorInfoEntries),
    [sponsorInfoEntries]
  );

  const handleAddRegulatoryEntry = (typeOverride) => {
    const type = typeOverride || selectedRegulatoryType;
    if (!type) return;
    const def = getRegulatoryIdentifierDef(type);
    if (!def) return;
    if (!def.allowMultiple && regulatoryIdentifierEntries.some((e) => e.type === type)) {
      return;
    }
    setHasSaved(false);
    setRegulatoryIdentifierEntries((prev) => [...prev, createEmptyRegulatoryEntry(type)]);
  };

  useEffect(() => {
    const available = availableRegulatoryTypesForDropdown(regulatoryIdentifierEntries);
    if (available.length === 0) return;
    if (!available.some((d) => d.key === selectedRegulatoryType)) {
      setSelectedRegulatoryType(available[0].key);
    }
  }, [regulatoryIdentifierEntries, selectedRegulatoryType]);

  const handleRegulatoryEntryChange = (entryId, value) => {
    setHasSaved(false);
    setRegulatoryIdentifierEntries((prev) =>
      prev.map((entry) => (entry.id === entryId ? { ...entry, value } : entry))
    );
  };

  const handleRemoveRegulatoryEntry = (entryId) => {
    setHasSaved(false);
    setRegulatoryIdentifierEntries((prev) => prev.filter((entry) => entry.id !== entryId));
  };

  const regulatoryEntryIndexByType = useMemo(() => {
    const counts = {};
    const indexMap = {};
    regulatoryIdentifierEntries.forEach((entry) => {
      counts[entry.type] = (counts[entry.type] || 0) + 1;
      indexMap[entry.id] = counts[entry.type];
    });
    return indexMap;
  }, [regulatoryIdentifierEntries]);

  const sponsorEntryIndexByType = useMemo(() => {
    const counts = {};
    const indexMap = {};
    sponsorInfoEntries.forEach((entry) => {
      counts[entry.type] = (counts[entry.type] || 0) + 1;
      indexMap[entry.id] = counts[entry.type];
    });
    return indexMap;
  }, [sponsorInfoEntries]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate required fields
    const sponsorSerialized = serializeSponsorEntries(sponsorInfoEntries);

    const requiredFields = {
      sponsor_investigational_product_codes: "Sponsor's Investigational Product Code(s)",
      investigational_product_names: 'Investigational Product Name(s)',
    };

    const missingFields = [];
    Object.keys(requiredFields).forEach((field) => {
      if (!formData[field] || formData[field].trim() === '') {
        missingFields.push(requiredFields[field]);
      }
    });

    const sponsorPairs = sponsorSerialized.sponsor_entries || [];
    const hasSponsorName = resolveProjectSponsorName(currentProject)
      || sponsorPairs.some((e) => e.name && e.name.trim())
      || (sponsorSerialized.sponsor_name && sponsorSerialized.sponsor_name.trim());

    if (!hasSponsorName) {
      missingFields.push('Sponsor Name (required)');
    }

    const sponsorMissingAddress = sponsorPairs.some(
      (e) => e.name && e.name.trim() && !(e.address && e.address.trim())
    );
    if (hasSponsorName && sponsorMissingAddress) {
      missingFields.push('Sponsor Legal Address (required for each Sponsor with a name)');
    }

    if (missingFields.length > 0) {
      setError(`Please fill in the following required fields: ${missingFields.join(', ')}`);
      setLoading(false);
      return;
    }

    const dateValidationError = validateAdministrativeDates(formData);
    if (dateValidationError) {
      setError(dateValidationError);
      setLoading(false);
      return;
    }

    try {
      console.log('Submitting administrative form data:', formData);
      console.log('New fields check:', {
        sponsor_investigational_product_codes: formData.sponsor_investigational_product_codes,
        investigational_product_names: formData.investigational_product_names
      });
      
      const payload = withProjectIdentityFields(currentProject, {
        ...formData,
        ...sponsorSerialized,
        ...serializeRegulatoryEntries(regulatoryIdentifierEntries),
        projectId,
      });

      const response = await axios.post(`${API_URL}/administrative-info/submit`, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        }
      });

      console.log('Server response:', response.data);

      if (response.data.success) {
        setSuccess(true);
        setHasSaved(true);
        // Wait for 2 seconds to show the success message before navigating
        setTimeout(() => {
          if (onSubmit) {
            onSubmit(response.data.data);
          }
          // Set active tab to next tab
          if (window.setActiveTab) {
            window.setActiveTab(prev => prev + 1);
          }
        }, 2000);
      } else {
        const errorMsg = response.data.message || 'Failed to save form data. Please try again.';
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Error saving form:', err);
      const errorMsg = err.response?.data?.message || 
                      err.message || 
                      'Failed to save form data. Please check your connection and try again.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box 
      component="form" 
      onSubmit={handleSubmit} 
      sx={{ 
        mt: 2,
        position: 'relative',
        p: 4,
        borderRadius: 3,
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.05)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          background: 'rgba(255, 255, 255, 0.08)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: 3,
          padding: '1px',
          background: `linear-gradient(135deg, ${colors.primary[500]}33 0%, ${colors.primary.light}33 50%, ${colors.accent.teal}33 100%)`,
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          pointerEvents: 'none',
          zIndex: -1,
        },
        '& .MuiTextField-root, & .MuiFormControl-root': {
          transition: 'all 0.3s ease',
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.85)',
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            },
            '&.Mui-focused': {
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              boxShadow: `0 4px 16px ${colors.primary[500]}33`,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: `${colors.primary[500]}4D`,
              transition: 'border-color 0.3s ease',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: `${colors.primary[500]}80`,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.primary[500],
              borderWidth: '2px',
            },
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(0, 0, 0, 0.7)',
            transition: 'all 0.3s ease',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: colors.primary[500],
          },
        },
        '& .MuiSelect-select': {
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(10px)',
        },
      }}
    >
      <Typography 
        variant="h6" 
        gutterBottom
        sx={{
          color: colors.primary[500],
          fontWeight: 600,
          mb: 2,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateX(5px)',
            color: colors.primary[600],
          },
        }}
      >
        Administrative Information
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        {PROJECT_IDENTITY_SOURCE_HINT}
      </Alert>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <HoverField title={ADMIN_FIELD_HOVER_INFO.sponsor_protocol_identifier}>
            <TextField
              fullWidth
              label="Sponsor Protocol Identifier"
              name="sponsor_protocol_identifier"
              value={formData.sponsor_protocol_identifier}
              InputProps={{ readOnly: true }}
              helperText="From trial creation (Projects sidebar → Edit Trial)"
            />
          </HoverField>
        </Grid>

        {/* Sponsor Information */}
        <Grid item xs={12}>
          <Box
            sx={{
              mt: 3,
              mb: 2,
              pt: 2,
              borderTop: `2px solid ${colors.primary[500]}33`,
              transition: 'all 0.3s ease',
            }}
          >
            <HoverField title={ADMIN_FIELD_HOVER_INFO.sponsor_information_section}>
              <Typography
                variant="subtitle1"
                sx={{
                  color: colors.primary[500],
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.95rem',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: colors.primary[600],
                    transform: 'translateX(5px)',
                  },
                }}
              >
                Sponsor Information
              </Typography>
            </HoverField>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              p: 2,
              borderRadius: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
            }}
          >
            <HoverField title={ADMIN_FIELD_HOVER_INFO.sponsor_type}>
              <FormControl sx={{ minWidth: { xs: '100%', sm: 240 } }} required>
                <InputLabel id="sponsor-field-type-label">Type</InputLabel>
                <Select
                  labelId="sponsor-field-type-label"
                  value={selectedSponsorFieldType}
                  label="Type *"
                  onChange={(e) => setSelectedSponsorFieldType(e.target.value)}
                >
                  {SPONSOR_ADDABLE_TYPE_LIST.map((def) => (
                    <MenuItem key={def.key} value={def.key}>
                      {def.pairLabel}{def.required ? ' *' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </HoverField>
            <HoverField title={SPONSOR_ADD_HOVER_BY_TYPE[selectedSponsorFieldType]}>
              <Button
                type="button"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleAddSponsorInfoEntry()}
                sx={{ textTransform: 'none' }}
              >
                Add {getSponsorFieldDef(selectedSponsorFieldType)?.pairLabel || 'pair'}
              </Button>
            </HoverField>
            <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 100%' }}>
              Select a type (Sponsor, Co-Sponsor, Local Sponsor, Device Manufacturer, or Collaborator), then add name and address pairs. At least one <strong>Sponsor</strong> pair is required. Use &quot;Add another&quot; for multiple pairs of the same type.
            </Typography>
          </Box>
        </Grid>

        {sponsorGroups.map((group) => {
          const def = getSponsorFieldDef(group.type);
          if (!def) return null;
          const isSponsor = group.type === 'sponsor';
          return (
            <Grid item xs={12} key={group.type}>
              <Box
                sx={{
                  mt: 1.5,
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${colors.primary[500]}40`,
                  backgroundColor: 'rgba(255, 255, 255, 0.55)',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: colors.primary[600],
                      textTransform: 'uppercase',
                      letterSpacing: '0.4px',
                      fontSize: '0.8rem',
                    }}
                  >
                    {def.pairLabel}
                    {isSponsor ? ' *' : ''}
                  </Typography>
                  <HoverField title={SPONSOR_ADD_HOVER_BY_TYPE[group.type]}>
                    <Button
                      type="button"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleAddSponsorInfoEntry(group.type)}
                      sx={{ textTransform: 'none' }}
                    >
                      Add another {def.pairLabel}
                    </Button>
                  </HoverField>
                </Box>

                {group.entries.map((entry, entryIdx) => {
                  const pairIndex = sponsorEntryIndexByType[entry.id] || 1;
                  const isCanonicalSponsorNameField = isSponsor && pairIndex === 1;
                  const canonicalSponsorName = resolveProjectSponsorName(currentProject);
                  const canRemove = !(
                    isSponsor && countEntriesByType(sponsorInfoEntries, 'sponsor') <= 1
                  );
                  return (
                    <Box
                      key={entry.id}
                      sx={{
                        pt: entryIdx > 0 ? 2 : 0,
                        mt: entryIdx > 0 ? 2 : 0,
                        borderTop: entryIdx > 0 ? '1px solid rgba(0, 0, 0, 0.1)' : 'none',
                      }}
                    >
                      {group.entries.length > 1 && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 1,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            {def.pairLabel} {pairIndex}
                          </Typography>
                          {canRemove && (
                            <IconButton
                              type="button"
                              size="small"
                              color="error"
                              aria-label={`Remove ${def.pairLabel} ${pairIndex}`}
                              onClick={() => handleRemoveSponsorInfoEntry(entry.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      )}
                      {group.entries.length === 1 && canRemove && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                          <IconButton
                            type="button"
                            size="small"
                            color="error"
                            aria-label={`Remove ${def.pairLabel}`}
                            onClick={() => handleRemoveSponsorInfoEntry(entry.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <HoverField
                            title={
                              SPONSOR_NAME_HOVER_BY_TYPE[group.type] ||
                              ADMIN_FIELD_HOVER_INFO.sponsor_name_generic
                            }
                          >
                            <TextField
                              fullWidth
                              label={def.nameLabel}
                              value={isCanonicalSponsorNameField && canonicalSponsorName ? canonicalSponsorName : entry.name}
                              onChange={(e) =>
                                handleSponsorEntryChange(entry.id, 'name', e.target.value)
                              }
                              InputProps={
                                isCanonicalSponsorNameField && canonicalSponsorName
                                  ? { readOnly: true }
                                  : undefined
                              }
                              helperText={
                                isCanonicalSponsorNameField && canonicalSponsorName
                                  ? 'Edit via Projects sidebar → Edit Trial'
                                  : undefined
                              }
                              required={isSponsor}
                            />
                          </HoverField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <HoverField title={ADMIN_FIELD_HOVER_INFO.sponsor_legal_address}>
                            <TextField
                              fullWidth
                              label={def.addressLabel}
                              value={entry.address}
                              onChange={(e) =>
                                handleSponsorEntryChange(entry.id, 'address', e.target.value)
                              }
                              required={isSponsor}
                            />
                          </HoverField>
                        </Grid>
                      </Grid>
                    </Box>
                  );
                })}
              </Box>
            </Grid>
          );
        })}

        {/* Regulatory or Clinical Trial Identifier(s) Section */}
        <Grid item xs={12}>
          <Box
            sx={{
              mt: 3,
              mb: 2,
              pt: 2,
              borderTop: `2px solid ${colors.primary[500]}33`,
              transition: 'all 0.3s ease',
            }}
          >
            <HoverField title={ADMIN_FIELD_HOVER_INFO.regulatory_identifiers_section}>
              <Typography
                variant="subtitle1"
                sx={{
                  color: colors.primary[500],
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.95rem',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: colors.primary[600],
                    transform: 'translateX(5px)',
                  },
                }}
              >
                Regulatory or Clinical Trial Identifier(s)
              </Typography>
            </HoverField>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              p: 2,
              borderRadius: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
            }}
          >
            <FormControl
              sx={{ minWidth: { xs: '100%', sm: 280 } }}
              disabled={regulatoryDropdownOptions.length === 0}
            >
              <InputLabel id="regulatory-type-label">Identifier type</InputLabel>
              <Select
                labelId="regulatory-type-label"
                value={
                  regulatoryDropdownOptions.some((d) => d.key === selectedRegulatoryType)
                    ? selectedRegulatoryType
                    : regulatoryDropdownOptions[0]?.key || ''
                }
                label="Identifier type"
                onChange={(e) => setSelectedRegulatoryType(e.target.value)}
              >
                {regulatoryDropdownOptions.map((def) => (
                  <MenuItem key={def.key} value={def.key}>
                    {def.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              type="button"
              variant="outlined"
              startIcon={<AddIcon />}
              disabled={regulatoryDropdownOptions.length === 0}
              onClick={() => handleAddRegulatoryEntry()}
              sx={{ textTransform: 'none' }}
            >
              Add{' '}
              {getRegulatoryIdentifierDef(selectedRegulatoryType)?.label || 'identifier'}
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 100%' }}>
              Select an identifier type, then add the field and enter its value. Each type (except Other) can be added once; Other allows multiple entries.
            </Typography>
          </Box>
        </Grid>

        {regulatoryIdentifierEntries.length === 0 && (
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              No regulatory identifiers added yet. Choose a type above and click Add.
            </Typography>
          </Grid>
        )}

        {regulatoryIdentifierEntries.map((entry) => {
          const def = getRegulatoryIdentifierDef(entry.type);
          if (!def) return null;
          const pairIndex = regulatoryEntryIndexByType[entry.id] || 1;
          const fieldLabel = `${def.label}${pairIndex > 1 ? ` ${pairIndex}` : ''}`;
          return (
            <Grid item xs={12} sm={6} key={entry.id}>
              <Box sx={compactEntryRowSx}>
                <HoverField title={REGULATORY_IDENTIFIER_HOVER_BY_TYPE[entry.type]}>
                  <TextField
                    size="small"
                    fullWidth
                    label={fieldLabel}
                    value={entry.value}
                    onChange={(e) => handleRegulatoryEntryChange(entry.id, e.target.value)}
                    placeholder={def.placeholder || ''}
                  />
                </HoverField>
                <Box sx={compactEntryActionsSx}>
                  {def.allowMultiple && (
                    <Tooltip title={`Add another ${def.label}`}>
                      <IconButton
                        type="button"
                        size="small"
                        color="primary"
                        aria-label={`Add another ${def.label}`}
                        onClick={() => handleAddRegulatoryEntry(entry.type)}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={`Remove ${fieldLabel}`}>
                    <IconButton
                      type="button"
                      size="small"
                      color="error"
                      aria-label={`Remove ${def.label}`}
                      onClick={() => handleRemoveRegulatoryEntry(entry.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Grid>
          );
        })}

        {/* Sponsor Approval Section */}
        <Grid item xs={12}>
          <Box
            sx={{
              mt: 3,
              mb: 2,
              pt: 2,
              borderTop: `2px solid ${colors.primary[500]}33`,
              transition: 'all 0.3s ease',
            }}
          >
            <HoverField title={ADMIN_FIELD_HOVER_INFO.sponsor_approval_section}>
              <Typography
                variant="subtitle1"
                sx={{
                  color: colors.primary[500],
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.95rem',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: colors.primary[600],
                    transform: 'translateX(5px)',
                  },
                }}
              >
                Sponsor Approval
              </Typography>
            </HoverField>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <HoverField title={ADMIN_FIELD_HOVER_INFO.sponsor_approval_type}>
            <FormControl fullWidth>
              <InputLabel id="sponsor-approval-type-label">Sponsor Approval</InputLabel>
              <Select
                labelId="sponsor-approval-type-label"
                value={sponsorApprovalMode || ''}
                onChange={handleSponsorApprovalTypeChange}
                label="Sponsor Approval"
              >
                <MenuItem value="">
                  <em>Select sponsor approval type</em>
                </MenuItem>
                {SPONSOR_APPROVAL_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </HoverField>
        </Grid>

        {showSponsorApprovalDate && (
          <Grid item xs={12} md={6}>
            <HoverField title={ADMIN_FIELD_HOVER_INFO.sponsor_approval_date}>
              <TextField
                fullWidth
                label="Sponsor Approval Date"
                name="sponsor_approval_date"
                type="date"
                value={formData.sponsor_approval_date}
                onChange={handleChange}
                inputProps={{ min: minSelectableDate }}
                helperText="Select today or a future date"
                error={Boolean(
                  formData.sponsor_approval_date && isDateBeforeToday(formData.sponsor_approval_date)
                )}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </HoverField>
          </Grid>
        )}

        {showSponsorApprovalLocation && (
          <Grid item xs={12} md={6}>
            <HoverField title={ADMIN_FIELD_HOVER_INFO.sponsor_approval_location}>
              <TextField
                fullWidth
                label="Location of Sponsor Approval Date"
                name="sponsor_approval_location_text"
                value={formData.sponsor_approval_location_text}
                onChange={handleChange}
                placeholder="e.g., Section 12.1, page 2 of the protocol"
                multiline
                minRows={2}
              />
            </HoverField>
          </Grid>
        )}

        <Grid item xs={12}>
          <HoverField title={ADMIN_FIELD_HOVER_INFO.medical_expert_contact}>
            <TextField
              fullWidth
              label="Contact Information for Medical Expert"
              name="medical_expert_contact"
              value={formData.medical_expert_contact}
              onChange={handleChange}
              multiline
              rows={3}
            />
          </HoverField>
        </Grid>

        {/* Investigational Product Information */}
        <Grid item xs={12}>
          <Box sx={{ mt: 1, mb: 1 }}>
            <HoverField title={ADMIN_FIELD_HOVER_INFO.investigational_product_section}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: colors.primary[600] }}>
                Investigational Product Information
              </Typography>
            </HoverField>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <HoverField title={ADMIN_FIELD_HOVER_INFO.sponsor_investigational_product_codes}>
            <TextField
              fullWidth
              label="Sponsor's Investigational Product Code(s)"
              name="sponsor_investigational_product_codes"
              value={formData.sponsor_investigational_product_codes}
              onChange={handleChange}
              placeholder="e.g., ABC-123, XYZ-456"
              helperText="Enter product codes separated by commas"
              required
            />
          </HoverField>
        </Grid>

        <Grid item xs={12} md={6}>
          <HoverField title={ADMIN_FIELD_HOVER_INFO.investigational_product_names}>
            <TextField
              fullWidth
              label="Investigational Product Name(s)"
              name="investigational_product_names"
              value={formData.investigational_product_names}
              onChange={handleChange}
              placeholder="e.g., Drug A, Drug B"
              helperText="Enter product names separated by commas"
              required
            />
          </HoverField>
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              mt: 4,
              mb: 2,
            }}
          >
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              sx={{ 
                minWidth: 150,
                backgroundColor: colors.primary[500],
                backdropFilter: 'blur(10px)',
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                boxShadow: `0 4px 12px ${colors.primary[500]}4D`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: colors.primary[600],
                  transform: 'translateY(-2px)',
                  boxShadow: `0 6px 20px ${colors.primary[500]}66`,
                },
                '&:active': {
                  transform: 'translateY(0px)',
                },
                '&:disabled': {
                  background: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                },
              }}
              disabled={loading || hasSaved}
            >
              {loading ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 1, color: 'white' }} />
                  Saving...
                </>
              ) : hasSaved ? (
                'Submitted'
              ) : (
                'Submit'
              )}
            </Button>
          </Box>
          {hasSaved && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, textAlign: 'right' }}>
              Saved. Change any field above to submit again.
            </Typography>
          )}
        </Grid>
      </Grid>

      {/* Success Alert */}
      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionProps={{ timeout: 300 }}
      >
        <Alert 
          onClose={() => setSuccess(false)} 
          severity="success" 
          sx={{ 
            width: '100%',
            backgroundColor: colors.success,
            backdropFilter: 'blur(10px)',
            color: 'white',
            boxShadow: `0 4px 12px ${colors.success}4D`,
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: `0 6px 20px ${colors.success}66`,
              transform: 'translateY(-2px)',
            },
          }}
        >
          Administrative information saved successfully!
        </Alert>
      </Snackbar>

      {/* Error Alert */}
      <Snackbar
        open={error !== null}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionProps={{ timeout: 300 }}
      >
        <Alert 
          onClose={() => setError(null)} 
          severity="error" 
          sx={{ 
            width: '100%',
            backgroundColor: colors.error,
            backdropFilter: 'blur(10px)',
            color: 'white',
            boxShadow: `0 4px 12px ${colors.error}4D`,
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: `0 6px 20px ${colors.error}66`,
              transform: 'translateY(-2px)',
            },
          }}
        >
          {error}
        </Alert>
      </Snackbar>

      {/* Scroll to Top Button */}
      <ScrollToTop bottomOffset={24} rightOffset={24} />
    </Box>
  );
};

export default AdministrativeInfoForm; 